import { Div, TextInput, Select, NumberInput, MultilineInput, CollectionPrinter, Checkbox, Span, RefSerializer, DateInput, Label, Button } from "@tblabs/truffle";
import { ShopContentProvider } from "../../Services/Content/ShopContentProvider";
import { DescriptionEditor } from "../../Components/Description/DescriptionEditor";
import { v4 as uuidv4 } from 'uuid';
import { PriceFactor, PriceFactorMode, Product } from "../../Models/Product/Product";
import { Tab } from "../../Models/Product/Tab";
import { RentPrice } from "../../Models/Product/RentPrice";
import { GalleryItem } from "../../Models/Description/Gallery/GalleryItem";
import { GalleryItemEditor } from "../../Components/Description/Gallery/GalleryItemEditor";
import { PicsUploader } from "../../Components/Description/Gallery/PicsUploader";
import { QuantityDiscount } from "../../Models/Product/QuantityDiscount";
import { BasketItemType } from "../../Models/Basket/BasketItemType";
import { ProductOption } from "../../Models/Product/ProductOption";
import { DangerButton, DefaultButton } from "../../Components/Utils/DangerButton";
import { PrimaryButton } from "../../Components/Utils/PrimaryButton";
import { SvgEditor } from "../../Components/Utils/SvgEditor";
import { Notification } from "../../Components/Notification";
import { AdminPage } from "./Page";
import { PanelHeader } from "./PanelHeader";
import { Panel } from "./Panel";
import { Swaper } from "./Swaper";
import { TableViewer } from "./TableViewer";
import { FormPrinter } from "./InputsPrinter";
import { InputRow } from "../../Components/Utils/InputRow";
import { ColumnBox } from "./ColumnBox";

export class ProductEditPage extends AdminPage
{
    constructor(private _shop: ShopContentProvider, private productUrl?: string)
    {
        super("Edycja produktu");

        this.Content(async (body) =>
        {
            await this._shop.Load();

            const product = this.productUrl
                ? this._shop.ProductByUrl(this.productUrl)
                : new Product({ Id: uuidv4() });

            this.SetTitle(product.Title.value + " - edycja");

            const uploader = new PicsUploader((images) =>
                product.Images.Add(
                    ...images.map(x => new GalleryItem({ NormalizedUrl: x.Normalized, ThumbnailUrl: x.Thumbnail, MiniUrl: x.Mini }))))
                .Hide()

            body.Append(
                new Panel("ℹ️ Meta", [
                    new FormPrinter([
                        new InputRow("Id", new Span(product.Id || "(brak id)")),
                        new InputRow("Widoczny na stronie", new Checkbox(product.Visible)),
                        new InputRow("Status", new TextInput(product.StatusLabel)),
                        new InputRow("Kolor statusu", new Select(product.StatusColor, Product.StatusColors)),
                        new InputRow("Pokaż zniżki", new Checkbox(product.ShowDiscounts)),
                        new InputRow("Pokaż dodatkowe informacje", new Checkbox(product.ShowExtraInfo)),
                        new InputRow("Ocena", new NumberInput(product.Rating).Step(0.01)),
                        new InputRow("Do wypożyczenia", new Checkbox(product.Rentable)),
                        new InputRow("Na sprzedaż", new Checkbox(product.Saleable)),
                        new InputRow("Dodaj do koszyka", new Div().Append(new Checkbox(product.Basketable).WidthAuto(), " jako ", new Select(product.AddToBasketAs, BasketItemType).WidthAuto())),
                        new InputRow("Kategoria produktu", new Select(product.CategoryId, this._shop.ProductCategoriesAsTitleIdObject)),
                        new InputRow("Tytuł", new TextInput(product.Title).Placeholder("Nazwa widoczna")),
                        new InputRow("Podpis", new TextInput(product.Subtitle).Placeholder("Podpis pod nazwą")),
                        new InputRow("Url", new TextInput(product.Url).Placeholder("Nazwa w url (bez spacji i polskich znaków!)")),
                        new InputRow("Nazwa w koszyku", new TextInput(product.BasketName).Placeholder("Nazwa w koszyku klienta")),
                        new InputRow("Tagi (oddzielone spacją)", new TextInput(product.Tags).Placeholder("Lista tagów po spacji")),
                        new InputRow("Ficzery (od nowej linii)", new MultilineInput(product.Features).Rows(8)),
                        new InputRow("Link do instrukcji", new TextInput(product.TutorialLink).Placeholder("Link do tutoriala")),
                    ]),
                ]),

                new Panel("🍟 Opcje produktu", [
                    new TableViewer(product.Options.List)
                        .AddColumn("Url & Id", x => new TextInput(x.Url))
                        .AddColumn("Labelka", x => new TextInput(x.Label))
                        .AddColumn("Opis", x => new MultilineInput(x.Description).Rows(4))
                        .AddColumn("Faktor ceny", x => new NumberInput(x.PriceFactor).Step(0.01).Width(80))
                        .AddColumn("Opcje", x => new Swaper(product.Options.List, x))
                        .Build(),
                    new PrimaryButton("➕ Dodaj opcje produktu")
                        .OnClick(() => product.Options.List.Add(new ProductOption({ Label: "Opcja A", Url: "opcja-a", PriceFactor: 1, Description: "Opis opcji" })))
                ]),

                new Panel("💰 Cennik", [
                    new Panel("Ceny wynajmu", [
                        new TableViewer(product.RentPrices)
                            .AddColumn("Cena brutto", x => new NumberInput(x.Value))
                            .AddColumn("Za ile dni", x => new NumberInput(x.Days))
                            .AddColumn("Widoczność", x => new Div().Append(new Checkbox(x.Visible)).Width(100).Background("red"))
                            .AddColumn("Opcje", x => new Swaper(product.RentPrices, x))
                            .Build(),
                        new PrimaryButton("➕ Dodaj cene wynajmu")
                            .OnClick(() => product.RentPrices.Add(new RentPrice({ Value: 1, Days: 1 }))),
                    ]),
                    new Panel("Cena zakupu", [
                        new NumberInput(product.SalePrice),
                    ]),
                    new Panel("Kaucja", [
                        new NumberInput(product.Deposit),
                    ]),
                    new Panel("Zniżki od ilości", [
                        new TableViewer(product.Discounts)
                            .AddColumn("Ilość", x => new NumberInput(x.Quantity))
                            .AddColumn("Procent zniżki", x => new NumberInput(x.Percent))
                            .AddColumn("Opcje", x => new Swaper(product.Discounts, x))
                            .Build(),
                        new PrimaryButton("➕ Dodaj zniżke")
                            .OnClick(() => product.Discounts.Add(new QuantityDiscount({ Quantity: 2, Percent: 10 }))),
                    ]),
                    new Panel("Cennik dedykowany", [
                        new TableViewer(product.PriceFactor)
                            .AddColumn("Daty", x => new Div().Append(new DateInput(x.Start).WidthAuto(), " - ", new DateInput(x.End).WidthAuto()))
                            .AddColumn("Tryb", x => new Select(x.Mode, Object.values(PriceFactorMode)))
                            .AddColumn("Wartość", x => new NumberInput(x.Value))
                            .AddColumn("Informacje", x => new MultilineInput(x.Info).Rows(3))
                            .AddColumn("Opcje", x => new Swaper(product.PriceFactor, x))
                            .Build(),
                        new PrimaryButton("➕ Dodaj mnożnik")
                            .OnClick(() => product.PriceFactor.Add(new PriceFactor()))
                    ]),
                ]),

                new Panel("📷 Galeria", [
                    new CollectionPrinter(product.Images, x => new GalleryItemEditor(x, product.Images)),
                    uploader,
                    new Div().ClearBoth(),
                    new PrimaryButton("➕ Dodaj zdjęcia").OnClick(() => uploader.Show())
                ]),

                new Panel("📝 Opis", [
                    new Panel("Taby opisu", [
                        new TableViewer(product.Tabs)
                            .AddColumn("Id", x => new TextInput(x.Id))
                            .AddColumn("Tytuł taba", x => new TextInput(x.Label))
                            .AddColumn("Ikona jako tekst", x => new SvgEditor(x.Icon))
                            .AddColumn("Widoczność", x => new Checkbox(x.IsVisible, "Widoczny na stronie"))
                            .AddColumn("", x => new Swaper(product.Tabs, x))
                            .Build(),
                        new PrimaryButton("➕ Dodaj tab")
                            .OnClick(() => product.Tabs.Add(new Tab({ Label: "Tab A", Icon: "", IsVisible: true }))),
                    ]),
                    new Panel("Pola opisu", [
                        new DescriptionEditor(product.Description, product.Tabs),
                    ]),
                ]),

                new ColumnBox(
                    new PrimaryButton("Zapisz zmiany")
                        .OnClick(async (btn) => await this.SaveChanges(btn, product)),
                    this.productUrl && new DangerButton("Usuń produkt")
                        .OnClick(async (btn) => await this.DeleteProduct(btn, product)),
                    new DefaultButton("Duplikuj produkt")
                        .OnClick(async (btn) => await this.DuplicateProduct(btn, product)),
                ),
            )
        });
    }

    private async SaveChanges(btn: Button, product: Product): Promise<void>
    {
        btn.Disabled();
        if (!this.productUrl) // is new
        {
            this._shop.Shop.Products.Add(product);
        }
        const result = await this._shop.SaveChanges();
        new Notification(result.IsSuccess ? "Zmiany zapisane!" : "❌ Problem z zapisaniem zmian", result.ErrorMessage || (result.ContentSize.toFixed(2) + "kb"));
        window.location.hash = `produkt/${product.Url.value}`;
        btn.Enabled();
    }

    private async DeleteProduct(btn: Button, product: Product): Promise<void>
    {
        btn.Disabled();
        if (confirm("Czy na pewno chcesz usunąć ten produkt? Możesz go też tylko ukryć."))
        {
            this._shop.Shop.Products.Remove(product);
            const result = await this._shop.SaveChanges();
            new Notification(result.IsSuccess ? "Zmiany zapisane!" : "❌ Problem z usunięciem", result.ErrorMessage || (result.ContentSize.toFixed(2) + "kb"));
            const category = this._shop.Shop.ProductCategories.Items.find(x => x.Id.value == product.CategoryId.value)
            window.location.hash = `kategoria/${category?.Url?.value}`;
        }
        btn.Enabled();
    }

    private async DuplicateProduct(btn: Button, product: Product): Promise<void>
    {
        btn.Disabled();
        const newProduct = new Product(RefSerializer.Flatenize(product));
        newProduct.Id = uuidv4();
        newProduct.Title.value += " (duplikat)";
        newProduct.Url.value += "-duplikat";
        this._shop.Shop.Products.Add(newProduct);
        const result = await this._shop.SaveChanges();
        new Notification(result.IsSuccess ? "Duplikat utworzony!" : "❌ Problem z utworzeniem duplikatu", result.ErrorMessage || (result.ContentSize.toFixed(2) + "kb"));
        btn.Enabled();
    }
}
